import {legacySourceFingerprint, legacyHash} from './legacyRecoverySources.js';
import {isCurrentWebVisitRecord} from './webCurrentRecords.js';

export const WEB_VISIT_DATABASE = 'coordinacion_visitas_web_visitas_v1';
const RECEIPT = 'web-authority-bootstrap:v1';
const SOURCE = 'coordinacion_visitas_offline';
const same = (a,b) => JSON.stringify(a) === JSON.stringify(b);

// Solo las visitas cambian de espacio. Jornada, GPS y RRHH conservan su base.
// La versión 11 del origen impide que un bundle antiguo (versión 10) vuelva a
// abrirlo para escribir. Si queda una pestaña antigua abierta, el upgrade falla
// visible; nunca se publican datos mientras haya escritores incompatibles.
export class WebVisitAuthorityDriver {
    constructor({Driver, stores, lock, onPhase = async () => {}}) {
        this.stores = stores; this.origin = new Driver(SOURCE,11);
        this.visits = new Driver(WEB_VISIT_DATABASE,10);
        this.visitStores = new Set(['ENTRIES','OPERATIONS','MEDIA','FILES','CONTEXTS','LISTS','ACTIVOS_CATALOG'].map(k=>stores[k]));
        this.lock = lock || (task => {
            if (!globalThis.navigator?.locks?.request) throw Error('Este navegador no permite coordinar el almacenamiento entre pestañas.');
            return navigator.locks.request('ifci-web-visit-authority-v1', {mode:'exclusive'}, task);
        });
        this.onPhase = onPhase; this.effectiveEngine = 'uninitialized'; this.pending = null;
        this.webVisitAuthority = true;
        this.strict = true; // Nunca escribir en una cola antigua si falla la autoridad nueva.
    }
    async snapshotOrigin() {
        const rows = [];
        for (const storeName of this.visitStores) for (const value of await this.origin.getAll(storeName))
            rows.push({storeName, key: storeName===this.stores.ENTRIES?value.key:value.id,
                fingerprint:await legacySourceFingerprint(`${SOURCE}/${storeName}`,value), value});
        return rows.sort((a,b)=>{const x=`${a.storeName}:${a.key}`,y=`${b.storeName}:${b.key}`;return x<y?-1:x>y?1:0;});
    }
    async init() {
        if (this.effectiveEngine === 'indexeddb') return this;
        if (this.pending) return this.pending;
        this.pending = this.lock(async () => {
            await this.origin.init(); await this.visits.init();
            let receipt = await this.visits.get(this.stores.CONTEXTS,RECEIPT);
            if (!receipt) {
                const rows = await this.snapshotOrigin(), items = [];
                const types = new Map(['ENTRIES','OPERATIONS','MEDIA','FILES'].map(k=>[this.stores[k],k]));
                for (const row of rows) {
                    const type = types.get(row.storeName);
                    let current = !type;
                    try {
                        if (type) current = isCurrentWebVisitRecord(type,row.value,
                            rows.find(r=>r.storeName===this.stores.FILES&&r.key===row.key)?.value);
                    } catch { current = false; }
                    if (current) items.push(row);
                }
                const sourceRows = rows.map(({storeName,key,fingerprint})=>({storeName,key,fingerprint}));
                const imported=items.map(({storeName,key,fingerprint})=>({storeName,key,fingerprint}));
                receipt = {id:RECEIPT,kind:'web_authority_bootstrap',schemaVersion:1,state:'copied',
                    source:SOURCE,destination:WEB_VISIT_DATABASE,sourceRows,
                    imported,version:await legacyHash(JSON.stringify({sourceRows,imported}))};
                if (!await this.visits.commitWebAuthorityBootstrap({stores:[...this.visitStores],items,receipt}))
                    throw Error('El destino web ya tiene datos sin una copia de origen comprobada. Se conservan ambas bases.');
                await this.onPhase({phase:'copied'});
            }
            if (receipt.kind!=='web_authority_bootstrap'||receipt.schemaVersion!==1||receipt.source!==SOURCE
                ||receipt.destination!==WEB_VISIT_DATABASE||!['copied','published'].includes(receipt.state)
                ||!Array.isArray(receipt.imported)||!Array.isArray(receipt.sourceRows)
                ||receipt.version!==await legacyHash(JSON.stringify({sourceRows:receipt.sourceRows,imported:receipt.imported}))
                ||receipt.imported.some(row=>!receipt.sourceRows.some(source=>same(source,row)))) throw Error('La evidencia de separación web requiere revisión.');
            if (receipt.state === 'copied') {
                const current = (await this.snapshotOrigin()).map(({storeName,key,fingerprint})=>({storeName,key,fingerprint}));
                if (!same(current,receipt.sourceRows)) throw Error('El origen web cambió durante la copia. Se conservan ambas bases sin activar la nueva.');
                for (const row of receipt.imported) {
                    const copy = await this.visits.get(row.storeName,row.key);
                    if (!copy || await legacySourceFingerprint(`${SOURCE}/${row.storeName}`,copy)!==row.fingerprint)
                        throw Error('La copia web no coincide con el origen. No se habilitan envíos.');
                }
                await this.onPhase({phase:'verified'});
                const finalSource=(await this.snapshotOrigin()).map(({storeName,key,fingerprint})=>({storeName,key,fingerprint}));
                if(!same(finalSource,receipt.sourceRows))throw Error('El origen web cambió antes de publicar. Se conservan ambas bases.');
                const published = {...receipt,state:'published'};
                await this.visits.mutate(this.stores.CONTEXTS,RECEIPT,current=>same(current,receipt)?published:null);
                if (!same(await this.visits.get(this.stores.CONTEXTS,RECEIPT),published)) throw Error('No se confirmó la autoridad web.');
                receipt = published;
            }
            this.bootstrap = receipt; this.effectiveEngine = 'indexeddb'; return this;
        }).finally(()=>{this.pending=null;});
        return this.pending;
    }
    async importedSourceFingerprints() {await this.init();return new Set(this.bootstrap.imported.map(row=>row.fingerprint));}
    target(store) {return this.visitStores.has(store)?this.visits:this.origin;}
    assertWritable(store,key) {
        if(store===this.stores.CONTEXTS&&key===RECEIPT)throw Error('La evidencia de separación web es inmutable.');
    }
    async get(store,key) {await this.init();return this.target(store).get(store,key);}
    async getAll(store,filter=null,options) {await this.init();return this.target(store).getAll(store,filter,options);}
    async put(store,value) {await this.init();this.assertWritable(store,value.id);return this.target(store).put(store,value);}
    async mutate(store,key,change) {await this.init();this.assertWritable(store,key);return this.target(store).mutate(store,key,change);}
    async compareAndDelete(store,key,predicate) {await this.init();this.assertWritable(store,key);return this.target(store).compareAndDelete(store,key,predicate);}
    async delete(store,key) {await this.init();this.assertWritable(store,key);return this.target(store).delete(store,key);}
    async clear(store) {await this.init();if(store===this.stores.CONTEXTS)throw Error('No se puede borrar la evidencia de separación web.');return this.target(store).clear(store);}
    async count(store,filter) {await this.init();return this.target(store).count(store,filter);}
    async atomicPutBatch(items) {
        await this.init(); if (!items.length) return [];
        const target=this.target(items[0].storeName);
        if(items.some(item=>this.target(item.storeName)!==target)) throw Error('La operación mezcla autoridades locales diferentes; no se guardó parcialmente.');
        for(const item of items)this.assertWritable(item.storeName,item.value.id);
        return target.atomicPutBatch(items);
    }
    async commitLegacyMigration(...args) {await this.init();return this.visits.commitLegacyMigration(...args);}
    async commitAttachmentContexts(...args) {await this.init();return this.visits.commitAttachmentContexts(...args);}
    async commitVisitaFinalization(...args) {await this.init();return this.visits.commitVisitaFinalization(...args);}
    async publishFinalizationSuccessor(...args) {await this.init();return this.visits.publishFinalizationSuccessor(...args);}
}
